import { RemoteData, RequestState } from '@new/common/utils/remote-data';
import {
  requestStateFailure,
  requestStateLoading,
  requestStateSuccess
} from '@new/common/utils/remote-data-utils';
import { assertIsDefined } from '@new/common/utils/type-guards/is-defined';
import { Selector } from '@ngxs/store';
import {
  CategoryFragment,
  DimensionFragment
} from '../services/load-dimensions.generated';
import { SubjectsListElementFragment } from '../services/load-subjects.generated';
import { DimensionsState } from './pool-details/dimensions/dimensions.state';
import { SubjectsState } from './pool-details/subjects/subjects.state';

export type SubjectWithNamedDimensions = SubjectsListElementFragment & {
  categories: CategoryFragment[];
};

export class MetaSelectors {
  @Selector([SubjectsState.subjects, DimensionsState.dimensions])
  public static subjectCategoriesByDimension(
    _subjects: RemoteData<SubjectsListElementFragment[]>,
    _dimensions: RemoteData<DimensionFragment[]>
  ): RemoteData<SubjectWithNamedDimensions[]> {
    const subjectsData = _subjects.data || [];
    const dimensionsData = _dimensions.data || [];

    const data: SubjectWithNamedDimensions[] = subjectsData.map(subject => {
      const categories = subject.categories.map(category => {
        const dimension =
          dimensionsData.find(d => d.id === category.dimension.id) ??
          category.dimension;

        assertIsDefined(dimension, `Dimension not found`);

        return { ...category, dimension: dimension };
      });

      return {
        id: subject.id,
        name: subject.name,
        categories
      };
    });

    const isLoading =
      requestStateLoading(_subjects) || requestStateLoading(_dimensions);
    const isFailure =
      requestStateFailure(_subjects) || requestStateFailure(_dimensions);
    const isSuccess =
      requestStateSuccess(_subjects) && requestStateSuccess(_dimensions);

    let requestState: RequestState = 'initial';

    if (isLoading) {
      requestState = 'loading';
    } else if (isFailure) {
      requestState = 'failure';
    } else if (isSuccess) {
      requestState = 'success';
    }

    return {
      data,
      requestState
    };
  }
}
